import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  CreditCard, Loader2, Save, CheckCircle, AlertCircle, Eye, EyeOff,
  ExternalLink
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { getPracticeId } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPaymentProcessing = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  const [stripeConfig, setStripeConfig] = useState({
    enabled: true,
    publicKey: '',
    secretKey: '',
    webhookSecret: '',
    testMode: true
  });

  const [paypalConfig, setPaypalConfig] = useState({
    enabled: true,
    clientId: '',
    secret: '',
    testMode: true
  });

  const [squareConfig, setSquareConfig] = useState({
    enabled: false,
    applicationId: '',
    accessToken: '',
    locationId: '',
    testMode: true
  });

  useEffect(() => {
    fetchPaymentConfigs();
  }, []);

  const fetchPaymentConfigs = async () => {
    try {
      setLoading(true);
      const practiceId = getPracticeId();
      const response = await fetch(`${BACKEND_URL}/api/payment-config/${practiceId}`);
      if (response.ok) {
        const configs = await response.json();
        configs.forEach(config => {
          if (config.processor === 'stripe') {
            setStripeConfig({
              enabled: config.enabled,
              publicKey: config.apiKey || '',
              secretKey: '',
              webhookSecret: '',
              testMode: config.testMode
            });
          } else if (config.processor === 'paypal') {
            setPaypalConfig({
              enabled: config.enabled,
              clientId: config.apiKey || '',
              secret: '',
              testMode: config.testMode
            });
          } else if (config.processor === 'square') {
            setSquareConfig({
              enabled: config.enabled,
              applicationId: config.apiKey || '',
              accessToken: '',
              locationId: '',
              testMode: config.testMode
            });
          }
        });
      }
    } catch (error) {
      console.error('Error fetching payment configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStripe = async () => {
    setSaving(true);
    try {
      const practiceId = getPracticeId();
      const response = await fetch(`${BACKEND_URL}/api/payment-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practiceId,
          processor: 'stripe',
          enabled: stripeConfig.enabled,
          testMode: stripeConfig.testMode,
          apiKey: stripeConfig.publicKey,
          secretKey: stripeConfig.secretKey,
          webhookSecret: stripeConfig.webhookSecret
        })
      });
      
      if (response.ok) {
        toast({
          title: 'Stripe Configuration Saved',
          description: 'Your Stripe payment settings have been updated.'
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save Stripe configuration.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayPal = async () => {
    setSaving(true);
    try {
      const practiceId = getPracticeId();
      const response = await fetch(`${BACKEND_URL}/api/payment-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practiceId,
          processor: 'paypal',
          enabled: paypalConfig.enabled,
          testMode: paypalConfig.testMode,
          apiKey: paypalConfig.clientId,
          secretKey: paypalConfig.secret
        })
      });
      
      if (response.ok) {
        toast({
          title: 'PayPal Configuration Saved',
          description: 'Your PayPal payment settings have been updated.'
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save PayPal configuration.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSquare = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: 'Square Configuration Saved',
        description: 'Your Square payment settings have been updated.'
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save Square configuration.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const SecretInput = ({ id, value, onChange, placeholder }) => (
    <div className="relative">
      <Input
        id={id}
        type={showSecrets[id] ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10 font-mono text-sm"
      />
      <button
        type="button"
        onClick={() => toggleSecret(id)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {showSecrets[id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-payment-processing">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Payment Processing</h1>
        <p className="text-gray-600 mt-2">Configure payment gateways to accept payments from your clients</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading payment configurations...</span>
        </div>
      ) : (
        <>
          {/* Overview */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Stripe</p>
                    <p className="text-lg font-semibold">{stripeConfig.enabled ? 'Connected' : 'Not Connected'}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${stripeConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">PayPal</p>
                    <p className="text-lg font-semibold">{paypalConfig.enabled ? 'Connected' : 'Not Connected'}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${paypalConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Square</p>
                    <p className="text-lg font-semibold">{squareConfig.enabled ? 'Connected' : 'Not Connected'}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${squareConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

      {/* Stripe Configuration */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                Stripe
                {stripeConfig.testMode && <Badge variant="secondary">Test Mode</Badge>}
              </CardTitle>
              <CardDescription>Accept credit cards, Apple Pay, and Google Pay</CardDescription>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={stripeConfig.enabled}
                onChange={(e) => setStripeConfig({...stripeConfig, enabled: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Enabled</span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <ExternalLink className="h-4 w-4" />
            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">
              Get your API keys from Stripe Dashboard
            </a>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="stripe-public">Publishable Key</Label>
              <Input
                id="stripe-public"
                value={stripeConfig.publicKey}
                onChange={(e) => setStripeConfig({...stripeConfig, publicKey: e.target.value})}
                placeholder="pk_test_..."
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label htmlFor="stripe-secret">Secret Key</Label>
              <SecretInput
                id="stripe-secret"
                value={stripeConfig.secretKey}
                onChange={(e) => setStripeConfig({...stripeConfig, secretKey: e.target.value})}
                placeholder="sk_test_..."
              />
            </div>
          </div>

          <div>
            <Label htmlFor="stripe-webhook">Webhook Secret (optional)</Label>
            <SecretInput
              id="stripe-webhook"
              value={stripeConfig.webhookSecret}
              onChange={(e) => setStripeConfig({...stripeConfig, webhookSecret: e.target.value})}
              placeholder="whsec_..."
            />
            <p className="text-xs text-gray-500 mt-1">Required for receiving payment confirmations</p>
          </div>

          <div className="flex items-center justify-between pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={stripeConfig.testMode}
                onChange={(e) => setStripeConfig({...stripeConfig, testMode: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Test Mode (use test API keys)</span>
            </label>
            <Button onClick={handleSaveStripe} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Stripe Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* PayPal Configuration */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <svg className="h-6 w-6 text-[#0070ba]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                </svg>
                PayPal
                {paypalConfig.testMode && <Badge variant="secondary">Sandbox</Badge>}
              </CardTitle>
              <CardDescription>Accept PayPal payments and Pay Later options</CardDescription>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={paypalConfig.enabled}
                onChange={(e) => setPaypalConfig({...paypalConfig, enabled: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Enabled</span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <ExternalLink className="h-4 w-4" />
            <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer">
              Get your credentials from PayPal Developer Dashboard
            </a>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="paypal-client">Client ID</Label>
              <Input
                id="paypal-client"
                value={paypalConfig.clientId}
                onChange={(e) => setPaypalConfig({...paypalConfig, clientId: e.target.value})}
                placeholder="AW..."
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label htmlFor="paypal-secret">Secret</Label>
              <SecretInput
                id="paypal-secret"
                value={paypalConfig.secret}
                onChange={(e) => setPaypalConfig({...paypalConfig, secret: e.target.value})}
                placeholder="ED..."
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={paypalConfig.testMode}
                onChange={(e) => setPaypalConfig({...paypalConfig, testMode: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Sandbox Mode (use sandbox credentials)</span>
            </label>
            <Button onClick={handleSavePayPal} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save PayPal Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Square Configuration */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">S</span>
                </div>
                Square
                {squareConfig.testMode && <Badge variant="secondary">Sandbox</Badge>}
              </CardTitle>
              <CardDescription>Accept in-person and online payments with Square</CardDescription>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={squareConfig.enabled}
                onChange={(e) => setSquareConfig({...squareConfig, enabled: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Enabled</span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <ExternalLink className="h-4 w-4" />
            <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer">
              Get your credentials from Square Developer Dashboard
            </a>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="square-app">Application ID</Label>
              <Input
                id="square-app"
                value={squareConfig.applicationId}
                onChange={(e) => setSquareConfig({...squareConfig, applicationId: e.target.value})}
                placeholder="sq0idp-..."
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label htmlFor="square-token">Access Token</Label>
              <SecretInput
                id="square-token"
                value={squareConfig.accessToken}
                onChange={(e) => setSquareConfig({...squareConfig, accessToken: e.target.value})}
                placeholder="EAAAl..."
              />
            </div>
          </div>

          <div>
            <Label htmlFor="square-location">Location ID</Label>
            <Input
              id="square-location"
              value={squareConfig.locationId}
              onChange={(e) => setSquareConfig({...squareConfig, locationId: e.target.value})}
              placeholder="L..."
              className="mt-1 font-mono text-sm"
            />
          </div>

          <div className="flex items-center justify-between pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={squareConfig.testMode}
                onChange={(e) => setSquareConfig({...squareConfig, testMode: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Sandbox Mode</span>
            </label>
            <Button onClick={handleSaveSquare} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Square Settings
            </Button>
          </div>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
};

export default AdminPaymentProcessing;
